import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../lib/utils";

interface Props {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  /** How many page numbers to show on each side of the current page. Default 1. */
  siblings?: number;
  className?: string;
}

const ELLIPSIS = "…" as const;

/**
 * Builds the visible page list with a constant number of slots
 * (2 × siblings + 5): the first and last page, the current page with
 * `siblings` neighbours, and an ellipsis for each skipped gap. Near an edge
 * the window slides instead of shrinking, so the user always sees the same
 * amount of pages. With 59 pages and one sibling:
 *   page 1  → 1 2 3 4 5 … 59
 *   page 30 → 1 … 29 30 31 … 59
 *   page 59 → 1 … 55 56 57 58 59
 */
export function pageWindow(page: number, totalPages: number, siblings = 1): Array<number | typeof ELLIPSIS> {
  const slots = siblings * 2 + 5;
  const range = (from: number, to: number) => Array.from({ length: to - from + 1 }, (_, i) => from + i);
  if (totalPages <= slots) return range(1, totalPages);
  const showLeftGap = page - siblings > 3;
  const showRightGap = page + siblings < totalPages - 2;
  if (!showLeftGap) return [...range(1, slots - 2), ELLIPSIS, totalPages];
  if (!showRightGap) return [1, ELLIPSIS, ...range(totalPages - (slots - 2) + 1, totalPages)];
  return [1, ELLIPSIS, ...range(page - siblings, page + siblings), ELLIPSIS, totalPages];
}

/**
 * Numbered pager for list pages: Prev · 1 … 4 [5] 6 … 59 · Next, plus a
 * "go to page" box once the list is long enough that the window hides pages,
 * so any page is one jump away. Renders nothing for a single page.
 * (shared/Pagination.tsx is the older pager with an incompatible API.)
 */
export default function PagerNav({ page, totalPages, onChange, siblings = 1, className }: Props) {
  const [jump, setJump] = useState("");
  // Clear a half-typed jump when the page changes from elsewhere (filters reset to page 1, etc.).
  useEffect(() => { setJump(""); }, [page, totalPages]);

  if (totalPages <= 1) return null;

  const items = pageWindow(page, totalPages, siblings);
  const showJump = items.includes(ELLIPSIS);
  const go = (p: number) => { if (p >= 1 && p <= totalPages && p !== page) onChange(p); };
  const submitJump = () => {
    const n = Number(jump);
    if (Number.isInteger(n)) go(Math.min(totalPages, Math.max(1, n)));
    setJump("");
  };

  const iconBtn =
    "p-2 rounded-lg border border-border bg-surface text-muted-foreground hover:text-foreground hover:bg-surface-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors";
  const numBtn = "min-w-[34px] h-[34px] px-2 rounded-lg text-xs font-medium transition-colors";

  return (
    <nav aria-label="Pagination" className={cn("flex items-center justify-end gap-1.5 flex-wrap", className)}>
      <button type="button" onClick={() => go(page - 1)} disabled={page <= 1} className={iconBtn} aria-label="Previous page">
        <ChevronLeft className="w-4 h-4" />
      </button>

      {items.map((item, i) =>
        item === ELLIPSIS ? (
          <span key={`e${i}`} className="min-w-[24px] text-center text-xs text-muted-foreground select-none" aria-hidden="true">
            {ELLIPSIS}
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => go(item)}
            aria-current={item === page ? "page" : undefined}
            aria-label={`Page ${item}`}
            className={cn(
              numBtn,
              item === page
                ? "bg-primary text-primary-foreground shadow-sm"
                : "border border-border bg-surface text-muted-foreground hover:text-foreground hover:bg-surface-muted",
            )}
          >
            {item}
          </button>
        ),
      )}

      <button type="button" onClick={() => go(page + 1)} disabled={page >= totalPages} className={iconBtn} aria-label="Next page">
        <ChevronRight className="w-4 h-4" />
      </button>

      {showJump && (
        <label className="ml-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          Go to
          <input
            type="number"
            min={1}
            max={totalPages}
            value={jump}
            onChange={(e) => setJump(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitJump(); } }}
            onBlur={() => { if (jump) submitJump(); }}
            placeholder={`1–${totalPages}`}
            aria-label="Go to page"
            className="w-[72px] h-[34px] px-2 rounded-lg border border-border bg-surface text-xs text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-ring [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
        </label>
      )}
    </nav>
  );
}
